/**
 * VersionHistoryModal.js
 *
 * Displays version history list with expandable details.
 * Users can view past version release notes.
 */
import React, { useState, useEffect } from 'react';
import { Modal, Button, Accordion, Badge, Spinner, Alert } from 'react-bootstrap';
import { useI18n } from '../../i18n';
import { versionAPI } from '../../utils/httpClient';
import styled from 'styled-components';

const ContentContainer = styled.div`
    padding: 0.75rem;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
    white-space: pre-wrap;
    font-size: 0.9rem;
    line-height: 1.5;
    max-height: 300px;
    overflow-y: auto;
`;

const VersionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const VersionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

const DateText = styled.span`
    color: #6c757d;
    font-size: 0.85rem;
`;

const VersionHistoryModal = ({ show, onHide }) => {
    const { t, lang } = useI18n();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch version history when modal opens
    useEffect(() => {
        if (show) {
            fetchVersionHistory();
        }
    }, [show]);

    const fetchVersionHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await versionAPI.getHistory(20);
            if (response.success) {
                setVersions(response.data || []);
            } else {
                setError(t('version:checkFailed', 'Failed to load version history'));
            }
        } catch (err) {
            console.error('Failed to fetch version history:', err);
            setError(err.message || t('version:checkFailed', 'Failed to load version history'));
        } finally {
            setLoading(false);
        }
    };

    // Format date based on language
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Simple markdown-like rendering
    const renderContent = (content) => {
        if (!content) return null;

        let html = content
            .replace(/^### (.+)$/gm, '<h4>$1</h4>')
            .replace(/^## (.+)$/gm, '<h3>$1</h3>')
            .replace(/^# (.+)$/gm, '<h2>$1</h2>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/`([^`]+)`/g, '<code>$1</code>')
            .replace(/^- (.+)$/gm, '• $1')
            .replace(/^\* (.+)$/gm, '• $1');

        return <div dangerouslySetInnerHTML={{ __html: html }} />;
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    📜 {t('version:viewHistory', 'Version History')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh' }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">
                            {t('common:status.loading', 'Loading...')}
                        </p>
                    </div>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : versions.length === 0 ? (
                    <Alert variant="info">
                        {t('version:noUpdates', "You're all caught up!")}
                    </Alert>
                ) : (
                    <Accordion defaultActiveKey="0">
                        {versions.map((version, index) => (
                            <Accordion.Item eventKey={String(index)} key={version.id || index}>
                                <Accordion.Header>
                                    <VersionHeader>
                                        <VersionInfo>
                                            <Badge bg={version.is_major ? 'danger' : 'primary'}>
                                                v{version.version}
                                            </Badge>
                                            <span>{version.title}</span>
                                        </VersionInfo>
                                        <DateText>
                                            {formatDate(version.release_date)}
                                        </DateText>
                                    </VersionHeader>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <ContentContainer>
                                        {renderContent(version.content)}
                                    </ContentContainer>
                                    {version.ai_generated && (
                                        <div className="mt-2">
                                            <Badge bg="info" className="small">
                                                {t('version:aiGenerated', 'AI Generated')}
                                            </Badge>
                                        </div>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t('common:actions.close', 'Close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default VersionHistoryModal;
